/**
 * SideMenu Component
 *
 * A reusable sidebar navigation component that matches the Ideoz design system.
 * Supports collapsible/expandable states, responsive design, accessibility features,
 * and dynamic navigation items.
 *
 * @module @ideoz/ui/components/molecules/SideMenu
 */

import React, { useState, useCallback, KeyboardEvent, useRef, useEffect } from 'react';
import { cn } from '../atoms/utils';
import { Badge } from '../atoms/badge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../atoms/tooltip';
import { UserMenuTemplate } from '../../components/templates/UserMenuTemplate';
import Icon from './Icon';
import type {
  SideMenuProps,
  SideMenuItemProps,
  SideMenuSectionProps,
  NavigationItem,
  UserMenuItem,
  User,
} from '../../types/SideMenu.types';
import MiniLogo from '../atoms/MiniLogo';
import Logo from '../atoms/Logo';
import { PanelLeft, Info, Plus, Trash2 } from 'lucide-react';

import { isPlanDetailsVisible, useAppSelector } from '@ideoz/core';
/**
 * Reusable base styles for interactive menu items
 * Used across navigation items, social links, and other interactive menu elements
 */
const MENU_ITEM_BASE_CLASSES =
  'group relative flex items-center gap-2 cursor-pointer transition-all duration-150 ease-out rounded-xl px-4 py-2 font-medium transition-colors text-muted-foreground hover:bg-secondary hover:text-foreground';
/**
 * Individual navigation item component
 */
const SideMenuItem: React.FC<SideMenuItemProps> = ({
  item,
  isCollapsed,
  isActive,
  onClick,
  testId,
}) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    },
    [onClick]
  );

  // Section header: just a small uppercase label, no button, no hover state
  if (item.type === 'header') {
    if (isCollapsed) return null;
    return (
      <div
        className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500"
        data-testid={testId}
      >
        {item.label}
      </div>
    );
  }

  // Shimmer placeholder row (e.g. recent chats loading).
  if (item.type === 'skeleton') {
    if (isCollapsed) return null;
    return (
      <div className="px-2 py-1.5" data-testid={testId}>
        <div className="h-4 w-full max-w-[85%] animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
      </div>
    );
  }

  // "New chat" is styled as a distinct bordered button (Lovable-style) instead
  // of a plain nav row — a full-width rounded box with a border and subtle
  // surface, set apart from the Files/Artifacts items below.
  const isNewChat = item.id === 'new-chat' || item.id === 'new-scope-conversation';

  const itemContent = (
    <button
      role="button"
      tabIndex={0}
      className={cn(
        isNewChat
          ? cn(
              'group relative flex items-center gap-2 cursor-pointer rounded-xl border shadow-sm transition-colors duration-150 bg-background',
              'border-gray-200 text-gray-900 hover:border-blue-200 mb-4',
              'dark:border-white/10 dark:bg-ink-surface dark:text-gray-100 dark:hover:bg-ink-raised',
              isCollapsed
                ? 'justify-center items-center h-8 w-8 before:content-[""] px-1 m-auto'
                : 'w-full px-3 py-2.5 text-sm font-medium'
            )
          : cn(
              MENU_ITEM_BASE_CLASSES,
              isActive
                ? 'text-accent-foreground bg-accent hover:text-accent-foreground hover:bg-accent'
                : '',
              // Nested under a parent mode item
              item.indent && !isCollapsed && 'ps-8',
              // Dynamic padding based on state
              isCollapsed
                ? 'justify-center items-center w-8 rounded-3xl before:content-[""] px-1 m-auto'
                : 'px-2'
            ),
        // Disabled styles (shared)
        item.disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
      )}
      data-active={isActive}
      onClick={item.disabled ? undefined : onClick}
      onKeyDown={item.disabled ? undefined : handleKeyDown}
      aria-label={item.label}
      aria-current={isActive ? 'page' : undefined}
      aria-disabled={item.disabled}
      data-testid={testId}
    >
      {/* Icon - always visible with smooth transition. New chat uses a plain
          plus to match the Lovable button. */}
      {(isNewChat || item.icon) && (
        <Icon
          icon={(isNewChat ? Plus : item.icon) as any}
          size={isNewChat ? 18 : 20}
          strokeWidth={isNewChat ? 2 : 1.5}
          className={cn('transition-colors duration-150 flex-shrink-0 stroke-[currentColor]')}
        />
      )}
      {/* Label with smooth fade + width animation */}
      <div
        className={cn(
          'flex items-center justify-between flex-1 whitespace-nowrap',
          isCollapsed ? 'w-0 opacity-0 flex-none' : 'w-auto max-w-full opacity-100'
        )}
      >
        <span className={cn('truncate text-sm')}>{item.label}</span>
        {item.badge && <Badge variant="secondary">{item.badge}</Badge>}
      </div>
    </button>
  );

  // Wrap with tooltip when collapsed
  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{itemContent}</TooltipTrigger>
        <TooltipContent className="px-2 py-1 text-xs" side="right">
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }

  // Rows with a delete handler (recent conversations) get a hover-revealed trash
  // button. It sits beside the main button (never nested, to keep valid HTML)
  // and stops propagation so deleting never also selects the row.
  if (item.onDelete) {
    return (
      <div className="group/row relative flex items-center [&>button]:min-w-0 [&>button]:flex-1">
        {itemContent}
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            item.onDelete?.();
          }}
          aria-label={item.deleteLabel ?? 'Delete'}
          className={cn(
            'absolute end-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 transition-opacity',
            'opacity-0 focus:opacity-100 group-hover/row:opacity-100',
            'hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10 dark:hover:text-red-400'
          )}
          data-testid={testId ? `${testId}-delete` : undefined}
        >
          <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      </div>
    );
  }

  return itemContent;
};

/**
 * Section wrapper component for grouping navigation items
 */
const SideMenuSection: React.FC<SideMenuSectionProps> = ({
  title,
  children,
  isCollapsed,
  className,
}) => {
  return (
    <div className={cn('space-y-1', className)}>
      {title && !isCollapsed && (
        <div className="px-3 py-2">
          <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {title}
          </h3>
        </div>
      )}
      {children}
    </div>
  );
};

/**
 * User avatar component
 * Renders user avatar with fallback to initials
 */
interface UserAvatarProps {
  user: User;
  isCollapsed?: boolean;
  className?: string;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ user, isCollapsed, className }) => {
  return (
    <div
      className={cn(
        'w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden bg-primary-900',
        className
      )}
    >
      {user.avatar ? (
        <img
          src={user.avatar}
          alt={user.name || 'User'}
          className="w-7 h-7 rounded-full object-cover"
        />
      ) : (
        <span className="text-white text-xs font-medium">
          {user.name ? user.name.charAt(0).toUpperCase() : 'ID'}
        </span>
      )}
    </div>
  );
};

/**
 * User info component
 * Renders user name and plan with fade animation
 */
interface UserInfoProps {
  user: User;
  isCollapsed?: boolean;
  showPlan?: boolean;
  className?: string;
}
const UserInfo: React.FC<UserInfoProps> = ({ user, isCollapsed, showPlan = true, className }) => {
  const planDetails = useAppSelector((state: any) => state.app.planDetails);
  const currentPlanName = planDetails?.name || 'Free';

  return (
    <div
      className={cn(
        'flex flex-col items-start min-w-0 overflow-hidden',
        'transition-all duration-150 ease-out',
        isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100',
        className
      )}
    >
      <span className="text-gray-700 dark:text-gray-200 truncate text-sm whitespace-nowrap">
        {user.name}
      </span>
      {showPlan && (
        <span className="text-xs font-normal text-gray-400 dark:text-gray-500 truncate whitespace-nowrap">
          {currentPlanName} plan
        </span>
      )}
    </div>
  );
};

/**
 * Main SideMenu component
 */
export const SideMenu: React.FC<SideMenuProps> = ({
  items,
  isCollapsed = false,
  onToggleCollapse,
  currentPath,
  socialLinks = [],
  user,
  onLogoClick,
  userMenuProps,
  className,
  testId = 'side-menu',
}) => {
  // State for user menu
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  // Ref to the user menu panel for focus management
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  // Items are rendered in the order they're passed in. The hook (useSidebarConfig) owns ordering
  // — re-grouping by type here would override page-specific layouts (e.g. the scope sidebar).

  // Handle user avatar click
  const handleUserAvatarClick = useCallback(() => {
    if (userMenuProps && user?.isLoggedin) {
      setIsUserMenuOpen(true);
    }
  }, [userMenuProps, user?.isLoggedin]);

  // Handle user menu close
  const handleUserMenuClose = useCallback(() => {
    setIsUserMenuOpen(false);
  }, []);

  // Close on Escape and manage focus when menu opens
  useEffect(() => {
    if (!isUserMenuOpen) return;

    const onKeyDown = (e: KeyboardEvent | globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        handleUserMenuClose();
      }
    };

    // Move focus to the menu container for accessibility
    setTimeout(() => {
      try {
        userMenuRef.current?.focus();
      } catch (err) {
        // ignore
      }
    }, 0);

    document.addEventListener('keydown', onKeyDown as any);
    return () => {
      document.removeEventListener('keydown', onKeyDown as any);
    };
  }, [isUserMenuOpen, handleUserMenuClose]);

  const handleItemClick = useCallback((item: NavigationItem) => {
    if (item.disabled) return;

    if (item.onClick) {
      item.onClick();
    }

    // Handle navigation if path is provided
    // Only use pushState in web context (not in plugin where origin is null/data:)
    if (item.path && typeof window !== 'undefined') {
      try {
        // Check if we're in a plugin context (data: URL or null origin)
        const isPluginContext =
          window.location.origin === 'null' ||
          window.location.protocol === 'data:' ||
          window.location.href.startsWith('data:');

        // Only use pushState if we're not in a plugin context and have a valid path
        if (!isPluginContext && item.path) {
          window.history.pushState({}, '', item.path);
        }
      } catch (error) {
        // Silently fail in plugin context where pushState is not supported
        console.debug('pushState not available in plugin context:', error);
      }
    }
  }, []);

  const isItemActive = useCallback(
    (item: NavigationItem) => {
      // If isActive is explicitly defined (true or false), use that value directly
      if (item.isActive !== undefined) {
        return item.isActive;
      }
      // If isActive is undefined, check path matching as fallback
      return currentPath ? item.path === currentPath : false;
    },
    [currentPath]
  );

  return (
    <TooltipProvider>
      <aside
        className={cn(
          // Base styles
          'flex flex-col pt-2 fixed start-0 top-0 bottom-0 z-40 transition-[width] duration-200 ease-out border-e border-gray-200 bg-surface-2 dark:border-gray-800',
          isCollapsed ? 'w-12' : 'w-[var(--sidebar-width)]',
          className
        )}
        aria-label="Main navigation"
        data-testid={testId}
      >
        {/* Header with cross-fade logo animation */}
        <header className="h-[52px] relative flex-shrink-0">
          {/* Collapsed state - Mini logo button that expands sidebar */}
          <div
            className={cn(
              'absolute inset-0 flex items-center justify-center',
              'transition-opacity duration-150 ease-out',
              isCollapsed ? 'opacity-100' : 'opacity-0 pointer-events-none'
            )}
          >
            <button
              onClick={onToggleCollapse}
              className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-150 group cursor-e-resize"
              aria-label="Expand sidebar"
              data-testid="sidebar-toggle-expand"
              data-track="sidebar_toggle"
            >
              <MiniLogo width={28} height={28} className="w-5 h-5 group-hover:hidden" />
              <PanelLeft className="w-5 h-5 text-gray-500 dark:text-gray-400 hidden group-hover:block" />
            </button>
          </div>

          {/* Expanded state - Full logo + collapse button */}
          <div
            className={cn(
              'absolute inset-0 flex items-center justify-between ps-6 pe-2',
              'transition-opacity duration-150 ease-out',
              isCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'
            )}
          >
            <button
              onClick={onLogoClick ? () => onLogoClick() : undefined}
              data-track="sidebar_logo"
            >
              <Logo width={80} height={20} />
            </button>
            <button
              onClick={onToggleCollapse}
              className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-150 cursor-w-resize"
              aria-label="Collapse sidebar"
              data-testid="sidebar-toggle-collapse"
              data-track="sidebar_toggle"
            >
              <Icon
                icon={PanelLeft as any}
                size={20}
                strokeWidth={1}
                className={cn('transition-colors duration-150 flex-shrink-0 stroke-[currentColor]')}
              />
              {/* <PanelLeft className="w-5 h-5 text-gray-500" /> */}
            </button>
          </div>
        </header>

        {/* Navigation Content */}
        {(() => {
          // Split at the `recent-header` item so recent chats render in their own <nav>.
          // Pages without that header (standard/project) put everything in the main nav.
          const recentStart = items.findIndex((i) => i.id === 'recent-header');
          const mainItems = recentStart === -1 ? items : items.slice(0, recentStart);
          const recentItems = recentStart === -1 ? [] : items.slice(recentStart);

          const renderItem = (item: NavigationItem) => {
            const itemType = item.type || 'navigation';
            const testIdPrefix =
              itemType === 'navigation' ? 'nav' : itemType === 'mode' ? 'mode' : 'action';
            return (
              <SideMenuItem
                key={item.id}
                item={item}
                isCollapsed={isCollapsed}
                isActive={isItemActive(item)}
                onClick={() => handleItemClick(item)}
                testId={`${testIdPrefix}-item-${item.id}`}
              />
            );
          };

          return (
            <>
              <nav
                className={cn(
                  'overflow-y-auto my-4 flex gap-1 flex-col',
                  isCollapsed ? 'px-1' : 'px-3'
                )}
              >
                {mainItems.map(renderItem)}
              </nav>
              {!isCollapsed && (
                <nav
                  id="sidebar-recent-chats"
                  className={cn(
                    'flex-1 overflow-y-auto my-4 flex gap-1 flex-col',
                    isCollapsed ? 'px-1' : 'px-2'
                  )}
                >
                  {recentItems.map(renderItem)}
                </nav>
              )}
            </>
          );
        })()}

        {/* Footer with Help Icon and User Profile */}
        {user && user.isLoggedin && (
          <footer
            className={cn(
              'flex gap-1 flex-col pb-1 mt-auto border-t border-gray-200 dark:border-gray-800',
              isCollapsed ? ' px-1' : ' px-2'
            )}
          >
            {/* User Profile - only show if logged in. Clicking opens the user menu */}
            <button
              type="button"
              onClick={handleUserAvatarClick}
              aria-label={user.name ? `${user.name}'s menu` : 'User menu'}
              aria-expanded={isUserMenuOpen}
              aria-controls="sidebar-user-menu"
              data-testid="sidebar-user-avatar"
              className={cn(
                MENU_ITEM_BASE_CLASSES,
                isCollapsed ? 'justify-center items-center w-10 before:content-[\"\"]' : 'px-2'
              )}
            >
              <UserAvatar user={user} isCollapsed={isCollapsed} />
              <UserInfo user={user} isCollapsed={isCollapsed} showPlan={isPlanDetailsVisible()} />
            </button>
          </footer>
        )}
      </aside>

      {/* Render the user menu as a fixed panel to the right of the sidebar */}
      {isUserMenuOpen && user && user.isLoggedin && userMenuProps && (
        <>
          {/* Backdrop: catches outside clicks and provides a target to close the menu */}
          <div
            onClick={handleUserMenuClose}
            aria-hidden="true"
            className="fixed inset-0 z-10 bg-transparent"
          />

          <div
            id="sidebar-user-menu"
            role="dialog"
            aria-modal="true"
            aria-label="User menu"
            ref={userMenuRef}
            tabIndex={-1}
            className={cn(
              `fixed top-0 h-full z-40 transition-[inset-inline-start] duration-200 ease-out start-[var(--sidebar-width)]`
            )}
          >
            <UserMenuTemplate
              onClose={handleUserMenuClose}
              points={userMenuProps.points}
              pointsLabel={userMenuProps.pointsLabel}
              menuItems={userMenuProps.menuItems}
              socialLinks={[]}
              onRequestUpgrade={userMenuProps.onRequestUpgrade}
              isRequestingUpgrade={userMenuProps.isRequestingUpgrade}
              canRequestUpgrade={userMenuProps.canRequestUpgrade}
              isWeb={userMenuProps.isWeb}
              className="w-[280px] lg:w-[320px]"
              testId="sidebar-user-menu"
            />
          </div>
        </>
      )}
    </TooltipProvider>
  );
};

export default SideMenu;
